<template>
  <div class="d-flex flex-column gap-4">
    <!-- Resume -->
    <v-card outlined rounded="lg" class="mt-4">
      <v-card-text class="d-flex flex-wrap justify-space-around align-center">
        <div class="text-center d-flex flex-column align-center px-4">
          <v-skeleton-loader
            v-if="loadingTickets"
            width="50"
            height="20"
            class="mb-1"
            type="card"
          />
          <h6 v-else class="mb-0">
            {{ reducedPayments.totalAmount | currency }}
          </h6>
          <span class="text-caption">Receita de vendas bruta</span>
        </div>
        <div class="text-center d-flex flex-column align-center px-4">
          <v-skeleton-loader
            v-if="loadingTickets"
            width="50"
            height="20"
            class="mb-1"
            type="card"
          />
          <h6 v-else class="mb-0">
            {{ -reducedPayments.totalPlatformFee | currency }}
          </h6>
          <span class="text-caption">Taxas da plataforma</span>
        </div>
        <div class="text-center d-flex flex-column align-center px-4">
          <v-skeleton-loader
            v-if="loadingTickets"
            width="50"
            height="20"
            class="mb-1"
            type="card"
          />
          <h6 v-else class="mb-0">
            {{
              (reducedPayments.totalAmount - reducedPayments.totalPlatformFee)
                | currency
            }}
          </h6>

          <span class="text-caption">Total líquido de vendas </span>
        </div>
        <div class="text-center d-flex flex-column align-center px-4">
          <v-skeleton-loader
            v-if="loadingResourceRequests"
            width="50"
            height="20"
            class="mb-1"
            type="card"
          />
          <h6 v-else class="mb-0">
            {{ -reducedResourceRequests.totalPartyTotalDebit | currency }}
          </h6>

          <span class="text-caption">Solicitações de recursos</span>
        </div>
        <div class="text-center d-flex flex-column align-center px-4">
          <v-skeleton-loader
            v-if="loadingAdjustments"
            width="50"
            height="20"
            class="mb-1"
            type="card"
          />
          <h6 v-else class="mb-0">
            {{ reducedAdjustments | currency }}
          </h6>

          <span class="text-caption">Ajustes</span>
        </div>
        <div class="text-center d-flex flex-column align-center px-4">
          <v-skeleton-loader
            v-if="loadingWithdrawals"
            width="50"
            height="20"
            class="mb-1"
            type="card"
          />
          <h6 v-else class="mb-0">
            {{
              -(reducedWithdrawals.paid + reducedWithdrawals.pending) | currency
            }}
          </h6>
          <span class="text-caption">Repasses</span>
        </div>
        <div class="text-center d-flex flex-column align-center px-4">
          <v-skeleton-loader
            v-if="
              loadingWithdrawals ||
              loadingTickets ||
              loadingResourceRequests ||
              loadingAdjustments
            "
            width="50"
            height="20"
            class="mb-1"
            type="card"
          />
          <h6
            v-else
            class="mb-0"
            :class="total >= 0 ? 'success--text' : 'error--text'"
          >
            {{ total | currency }}
          </h6>

          <span class="text-caption">Saldo</span>
        </div>
      </v-card-text>
    </v-card>

    <!-- tickets -->
    <v-card outlined rounded="lg" :loading="loadingTickets">
      <div class="pa-3">
        <div class="d-flex justify-space-between mb-2">
          <h6 class="mb-0">Pagamentos</h6>
          <v-btn @click="loadTickets" :disabled="loadingTickets" icon small>
            <v-icon small>mdi-refresh</v-icon>
          </v-btn>
        </div>
        <v-alert v-if="errorTickets" type="error" class="mb-0">
          {{ errorTickets }}
        </v-alert>

        <v-alert
          v-if="!tickets.length && !loadingTickets"
          type="info"
          class="mb-0"
          text
        >
          Nenhum pagamento registrado
        </v-alert>
        <template v-else>
          <v-alert
            v-if="reducedPayments.hasNotTaxedPayment"
            type="warning"
            class="mb-0"
          >
            Atenção: Há pagamentos que não foram taxados. Consulte a aba
            "Pagamentos" para ver mais detalhes.
          </v-alert>
          <v-simple-table dense>
            <thead>
              <tr>
                <th class="text-left">Método</th>
                <th class="text-center">Quantidade</th>
                <th class="text-right">Valor Bruto</th>
                <th class="text-right">Taxa</th>
                <th class="text-right">Valor Líquido</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="item in reducedPayments.items"
                :key="item.paymentMethod"
              >
                <td>{{ PAYMENT.paymentMethod[item.paymentMethod].text }}</td>
                <td class="text-center">{{ item.count }}</td>
                <td class="text-right">{{ item.amount | currency }}</td>
                <td
                  class="text-right"
                  :class="item.platformFee > 0 ? 'error--text' : ''"
                >
                  {{ (item.platformFee * -1) | currency }}
                </td>
                <td
                  class="text-right"
                  :class="
                    item.amount - item.platformFee > 0 ? 'primary--text' : ''
                  "
                >
                  {{ (item.amount - item.platformFee) | currency }}
                </td>
              </tr>
            </tbody>
            <td colspan="5">
              <v-divider />
              <v-divider />
            </td>
            <tfoot>
              <tr class="font-weight-bold">
                <td colspan="2">Total</td>
                <td class="text-right">
                  {{ reducedPayments.totalAmount | currency }}
                </td>
                <td class="text-right error--text">
                  {{ (reducedPayments.totalPlatformFee * -1) | currency }}
                </td>
                <td class="text-right primary--text">
                  {{
                    (reducedPayments.totalAmount -
                      reducedPayments.totalPlatformFee)
                      | currency
                  }}
                </td>
              </tr>
            </tfoot>
          </v-simple-table>
        </template>
      </div>
    </v-card>

    <!-- Resource Requests -->
    <v-card outlined rounded="lg" :loading="loadingResourceRequests">
      <div class="pa-3">
        <div class="d-flex justify-space-between mb-2">
          <h6 class="mb-0">Solicitações de Recursos</h6>
          <v-btn
            @click="loadResourceRequests"
            :disabled="loadingResourceRequests"
            icon
            small
          >
            <v-icon small>mdi-refresh</v-icon>
          </v-btn>
        </div>

        <v-alert v-if="errorResourceRequests" type="error" class="mb-0">
          {{ errorResourceRequests }}
        </v-alert>

        <v-alert
          v-if="!resourceRequests.length && !loadingResourceRequests"
          type="info"
          text
          class="mb-0"
        >
          Nenhuma solicitação de recurso encontrada
        </v-alert>
        <v-simple-table v-else dense>
          <thead>
            <tr>
              <th class="text-left">Codigo</th>
              <th class="text-left">Itens</th>
              <th class="text-right">Valor total</th>
              <th class="text-right">Valor para evento</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in reducedResourceRequests.items" :key="item.id">
              <td>{{ item.code }}</td>
              <td>{{ item.name }}</td>

              <td class="text-right">
                {{ item.totalAmount | currency }}
              </td>
              <td class="text-right">
                <small>
                  ({{
                    (item.partyTotalDebit / item.totalAmount) | percentage(0)
                  }})
                </small>
                <span :class="item.partyTotalDebit > 0 ? 'error--text' : ''">
                  {{ (item.partyTotalDebit * -1) | currency }}
                </span>
              </td>
            </tr>
          </tbody>
          <td colspan="4">
            <v-divider />
            <v-divider />
          </td>
          <tfoot>
            <tr class="font-weight-bold">
              <td colspan="2">Total</td>
              <td class="text-right">
                {{ reducedResourceRequests.totalAmount | currency }}
              </td>
              <td class="text-right error--text">
                <small>
                  ({{
                    (reducedResourceRequests.totalPartyTotalDebit /
                      reducedResourceRequests.totalAmount)
                      | percentage(0)
                  }})
                </small>
                {{
                  (reducedResourceRequests.totalPartyTotalDebit * -1) | currency
                }}
              </td>
            </tr>
          </tfoot>
        </v-simple-table>
      </div>
    </v-card>

    <!-- Adjustments -->
    <v-card outlined rounded="lg" :loading="loadingAdjustments">
      <div class="pa-3">
        <div class="d-flex justify-space-between mb-2 gap-2">
          <h6 class="mb-0">Ajustes</h6>
          <v-spacer />
          <v-btn
            @click="loadAdjustments"
            :disabled="loadingAdjustments"
            icon
            small
          >
            <v-icon small>mdi-refresh</v-icon>
          </v-btn>
          <v-btn @click="addAdjustment" color="primary" small>
            <v-icon small left>mdi-plus</v-icon>
            Ajuste
          </v-btn>
        </div>

        <v-alert v-if="errorAdjustments" type="error" class="mb-0">
          {{ errorAdjustments }}
        </v-alert>

        <v-alert
          v-if="!adjustments.length && !loadingAdjustments"
          type="info"
          class="mb-0"
          text
        >
          Nenhum ajuste registrado
        </v-alert>
        <v-simple-table v-else dense>
          <thead>
            <tr>
              <th class="text-left">Data</th>
              <th class="text-left">Descrição</th>
              <th class="text-left">Tipo</th>
              <th class="text-right">Valor</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in adjustments" :key="item.id">
              <td>{{ item.createdAt | date("DD/MM/YYYY HH:mm") }}</td>
              <td>{{ item.description }}</td>
              <td>{{ adjustmentsTypes[item.type] }}</td>
              <td
                class="text-right"
                :class="item.amount < 0 ? 'error--text' : 'primary--text'"
              >
                {{ item.amount | currency }}
              </td>
            </tr>
          </tbody>
          <td colspan="4">
            <v-divider />
            <v-divider />
          </td>
          <tfoot>
            <tr class="font-weight-bold">
              <td colspan="3">Total</td>
              <td
                class="text-right"
                :class="
                  reducedAdjustments > 0
                    ? 'primary--text'
                    : reducedAdjustments < 0
                    ? 'error--text'
                    : ''
                "
              >
                {{ reducedAdjustments | currency }}
              </td>
            </tr>
          </tfoot>
        </v-simple-table>
      </div>
    </v-card>

    <!-- Withdrawals -->
    <v-card outlined rounded="lg" :loading="loadingWithdrawals">
      <div class="pa-3">
        <div class="d-flex justify-space-between mb-2 gap-2">
          <h6 class="mb-0">Repasses</h6>
          <v-spacer />
          <div class="d-flex flex-column align-end">
            <div class="d-flex align-center gap-2">
              <v-btn
                @click="loadWithdrawals"
                :disabled="loadingWithdrawals"
                icon
                small
              >
                <v-icon small>mdi-refresh</v-icon>
              </v-btn>
              <v-btn @click="addWithdrawal" color="primary" small>
                <!-- :disabled="reducedWithdrawals.pending > 0" -->
                <v-icon small left>mdi-plus</v-icon>
                Repasse
              </v-btn>
            </div>
            <!-- <small v-if="reducedWithdrawals.pending > 0">
              Não é possível criar um repasse enquanto houver pendente.
            </small> -->
          </div>
        </div>

        <v-alert v-if="errorWithdrawals" type="error" class="mb-0">
          {{ errorWithdrawals }}
        </v-alert>

        <v-alert
          v-if="!withdrawals.length && !loadingWithdrawals"
          type="info"
          text
          class="mb-0"
        >
          Nenhum repasse registrado
        </v-alert>
        <v-simple-table v-else :loading="loadingWithdrawals" dense>
          <thead>
            <tr>
              <th class="text-left" style="min-width: 150px">Data</th>
              <th class="text-left" style="min-width: 300px; width: 100%">
                Observação
              </th>
              <th class="text-center" style="width: 100px">Comprovante</th>
              <th class="text-center" style="width: 100px">Tipo</th>
              <th class="text-center" style="width: 100px">Status</th>
              <th class="text-right" style="width: 120px">Valor</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in withdrawals" :key="item.id">
              <td>{{ item.createdAt | date("DD/MM/YYYY HH:mm") }}</td>
              <td>
                {{ item.observation }}
              </td>
              <td class="text-center">
                <v-btn
                  v-if="!item.proofDocument && item.status === 'PAID'"
                  text
                  x-small
                  icon
                  :disabled="!!loadingUploadProofDocument"
                  :loading="uploadProofDocumentId === item.id && loadingUploadProofDocument"
                  @click="initUploadProofDocument(item)"
                >
                  <v-icon small>mdi-upload</v-icon>
                </v-btn>
                <v-btn
                  v-if="item.proofDocument"
                  text
                  x-small
                  icon
                  :href="item.proofDocument"
                  target="_blank"
                >
                  <v-icon small>mdi-file-download</v-icon>
                </v-btn>
              </td>
              <td>
                <v-tooltip top>
                  <template #activator="{ on, attrs }">
                    <v-alert
                      :color="withdrawalsTypes[item.type].color"
                      class="text-12 mb-0 px-2"
                      style="
                        padding: 3px 12px;
                        width: 100px;
                        text-align: center;
                      "
                      v-bind="attrs"
                      v-on="on"
                      text
                      dense
                      label
                    >
                      {{ withdrawalsTypes[item.type].text }}
                    </v-alert>
                  </template>
                  <span>
                    {{ withdrawalsTypes[item.type].description }}
                  </span>
                </v-tooltip>
              </td>
              <td>
                <v-chip
                  :color="withdrawalsStatuses[item.status].color"
                  small
                  label
                  style="width: 90px; text-align: center"
                  class="justify-center"
                >
                  {{ withdrawalsStatuses[item.status].text }}
                </v-chip>
              </td>
              <td
                class="text-right"
                :class="
                  item.status === 'PAID' ? 'primary--text' : 'error--text'
                "
              >
                {{ item.amount | currency }}
              </td>
            </tr>
          </tbody>
          <td colspan="6">
            <v-divider />
            <v-divider />
          </td>
          <tfoot>
            <template v-if="reducedWithdrawals.pending > 0">
              <tr style="font-style: italic; font-weight: medium">
                <td colspan="5">Total pendente</td>
                <td class="text-right">
                  {{ reducedWithdrawals.pending | currency }}
                </td>
              </tr>
              <tr style="font-style: italic; font-weight: medium">
                <td colspan="5">Total pago</td>
                <td class="text-right">
                  {{ reducedWithdrawals.paid | currency }}
                </td>
              </tr>
            </template>
            <tr class="font-weight-bold">
              <td colspan="5">Total</td>
              <td class="text-right">
                {{
                  (reducedWithdrawals.paid + reducedWithdrawals.pending)
                    | currency
                }}
              </td>
            </tr>
          </tfoot>
        </v-simple-table>
      </div>
    </v-card>

    <input
      ref="uploadProofDocument"
      type="file"
      style="display: none"
      @change="uploadProofDocument"
      accept="image/*, application/pdf"
    />

    <!-- Modals -->
    <PartyAdjustmentsModal @success="loadAdjustments" :party="party" />
    <PartyWithdrawalsModal
      @success="loadWithdrawals"
      :party="party"
      :reducedAdjustments="reducedAdjustments"
      :reducedPayments="reducedPayments"
      :reducedResourceRequests="reducedResourceRequests"
      :reducedWithdrawals="reducedWithdrawals"
    />
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import PAYMENT from "@/utils/payment";
import PARTY from "@/services/admin/parties";
import { status } from "@/definitions/ResourceRequest.json";

import PartyAdjustmentsModal from "./modules/PartyAdjustmentsModal.vue";
import PartyWithdrawalsModal from "./modules/PartyWithdrawalsModal.vue";

const context = require.context("../resourceRequest/types", true, /\.vue$/);
const types = context.keys().reduce((acc, key) => {
  const { manifest, default: component } = context(key);
  acc[manifest.id] = {
    ...manifest,
    component,
  };
  return acc;
}, {});

export default {
  name: "PartyWithdrawal",
  props: {
    party: {
      type: Object,
      required: true,
    },
  },
  components: {
    PartyAdjustmentsModal,
    PartyWithdrawalsModal,
  },
  data() {
    return {
      PAYMENT,
      statuses: status,
      types,

      // Withdrawals
      loadingWithdrawals: true,
      errorWithdrawals: null,
      withdrawals: [],
      withdrawalsTypes: {
        FULL: {
          text: "Quitação",
          color: "primary",
          description: "Repasse total do saldo no momento do pagamento",
        },
        PARTIAL: {
          text: "Parcial",
          color: "info",
          description: "Repasse parcial do saldo no momento do pagamento",
        },
        ADVANCE: {
          text: "Adiantamento",
          color: "warning",
          description: "Repasse maior que o saldo no momento do pagamento",
        },
      },
      withdrawalsStatuses: {
        PAID: {
          text: "Pago",
          color: "success",
        },
        PENDING: {
          text: "Pendente",
          color: "warning",
        },
        REJECTED: {
          text: "Rejeitado",
          color: "error",
        },
        CANCELLED: {
          text: "Cancelado",
          color: "secondary",
        },
      },

      // Resource Requests
      loadingResourceRequests: true,
      errorResourceRequests: null,
      resourceRequests: [],

      // Adjustments
      loadingAdjustments: true,
      errorAdjustments: null,
      adjustments: [],
      adjustmentsTypes: {
        CASHBACK: "Cashback",
        REFUND: "Reembolso",
        DISCOUNT: "Desconto",
        BONUS: "Bônus",
        CREDIT: "Crédito",
        FEE: "Taxa",
        TAX: "Taxa",
        FINE: "Multa",
        DEBIT: "Débito",
        OTHER: "Outro",
      },

      // tickets
      loadingTickets: true,
      errorTickets: null,
      tickets: [],

      // Upload proof document
      uploadProofDocumentId: null,
      loadingUploadProofDocument: false,
      errorUploadProofDocument: null,
    };
  },
  mounted() {
    this.loadResourceRequests();
    this.loadWithdrawals();
    this.loadAdjustments();
    this.loadTickets();
  },
  methods: {
    async loadResourceRequests() {
      try {
        this.loadingResourceRequests = true;
        this.errorResourceRequests = null;

        const response = await PARTY.resourceRequests(this.party.id);
        this.resourceRequests = response.resourceRequests;
      } catch (error) {
        this.errorResourceRequests = error.message || error;
      } finally {
        this.loadingResourceRequests = false;
      }
    },
    async loadWithdrawals() {
      try {
        this.loadingWithdrawals = true;
        this.errorWithdrawals = null;

        const response = await PARTY.withdrawals(this.party.id);
        this.withdrawals = response.withdrawals;
      } catch (error) {
        this.errorWithdrawals = error.message || error;
      } finally {
        this.loadingWithdrawals = false;
      }
    },
    async loadAdjustments() {
      try {
        this.loadingAdjustments = true;
        this.errorAdjustments = null;

        const response = await PARTY.adjustments(this.party.id);
        this.adjustments = response.adjustments;
      } catch (error) {
        this.errorAdjustments = error.message || error;
      } finally {
        this.loadingAdjustments = false;
      }
    },
    async loadTickets() {
      try {
        this.loadingTickets = true;
        this.errorTickets = null;

        const response = await PARTY.tickets(this.party.id, {
          status: "succeeded",
        });

        this.tickets = response.tickets;
      } catch (error) {
        this.errorTickets = error.message || error;
      } finally {
        this.loadingTickets = false;
      }
    },
    addAdjustment() {
      this.$root.$emit("party-adjustments-modal");
    },
    addWithdrawal() {
      this.$root.$emit("party-withdrawals-modal");
    },
    async initUploadProofDocument(item) {
      this.uploadProofDocumentId = item.id;
      this.$refs.uploadProofDocument.click();
    },
    async uploadProofDocument(item) {
      if (!this.uploadProofDocumentId) return;
      try {
        this.loadingUploadProofDocument = true;
        const file = this.$refs.uploadProofDocument.files[0];
        if (!file) return;

        const confirm = window.confirm(
          `Tem certeza que deseja substituir o arquivo ${file.name} no repasse?`
        );
        if (!confirm) return;

        await PARTY.uploadWithdrawalProofDocument(
          this.party.id,
          this.uploadProofDocumentId,
          file
        );
        await this.loadWithdrawals();
      } catch (error) {
        this.errorUploadProofDocument = error.message || error;
      } finally {
        this.loadingUploadProofDocument = false;
        this.uploadProofDocumentId = null;
      }
    },
  },
  computed: {
    ...mapGetters("auth", ["hasPermission"]),
    reducedPayments() {
      let hasNotTaxedPayment = false;
      let totalAmount = 0;
      let totalPlatformFee = 0;
      const reduced = this.tickets.reduce((acc, ticket) => {
        const payment = ticket.Payment;
        // Hard filters
        if (payment.status !== "succeeded") return acc;
        if (payment.paymentType === "MONEY") return acc;

        // Soft filters
        let paymentMethod = payment.paymentMethod;
        if (payment.paymentType === "COURTESY") paymentMethod = "COURTESY";

        if (!acc[paymentMethod]) {
          acc[paymentMethod] = {
            paymentMethod,
            amount: 0,
            count: 0,
            platformFee: 0,
          };
        }

        acc[paymentMethod].count++;
        acc[paymentMethod].amount += ticket.amount;
        acc[paymentMethod].platformFee += ticket.platformFee;
        totalAmount += ticket.amount;
        totalPlatformFee += ticket.platformFee;
        if (
          paymentMethod !== "COURTESY" &&
          payment.paymentType !== "MONEY" &&
          payment.platformFee <= 0
        ) {
          hasNotTaxedPayment = true;
        }

        return acc;
      }, {});

      return {
        totalAmount,
        totalPlatformFee,
        hasNotTaxedPayment,
        items: Object.values(reduced),
      };
    },
    reducedResourceRequests() {
      let totalAmount = 0;
      let totalPartyTotalDebit = 0;
      const reduced = this.resourceRequests
        .filter((resourceRequest) =>
          ["APPROVED", "PAID"].includes(resourceRequest.status)
        )
        .map((resourceRequest) => {
          const amount = resourceRequest.items.reduce((acc, item) => {
            return acc + item.amount;
          }, 0);
          totalAmount += amount;
          totalPartyTotalDebit += resourceRequest.partyTotalDebit;
          return {
            ...resourceRequest,
            name: resourceRequest.items
              .map((item) => this.types[item.type].name)
              .join(", "),
            totalAmount: amount,
          };
        });

      return {
        totalAmount,
        totalPartyTotalDebit,
        items: reduced,
      };
    },
    reducedAdjustments() {
      return this.adjustments.reduce(
        (acc, adjustment) => acc + adjustment.amount,
        0
      );
    },
    reducedWithdrawals() {
      return this.withdrawals.reduce(
        (acc, withdrawal) => {
          const status = withdrawal.status.toLowerCase();
          if (acc[status] === undefined) acc[status] = 0;
          acc[status] += withdrawal.amount;
          return acc;
        },
        {
          pending: 0,
          paid: 0,
        }
      );
    },
    total() {
      return (
        this.reducedPayments.totalAmount -
        this.reducedPayments.totalPlatformFee -
        this.reducedResourceRequests.totalPartyTotalDebit +
        this.reducedAdjustments -
        this.reducedWithdrawals.paid -
        this.reducedWithdrawals.pending
      );
    },
    someError() {
      return (
        this.errorResourceRequests ||
        this.errorAdjustments ||
        this.errorWithdrawals ||
        this.errorTickets
      );
    },
  },
};
</script>
